import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { validatePassword, passwordCheckBreakdown } from "../lib/passwordPolicy";
import WelcomePopup from "../components/WelcomePopup";
import PasswordInput from "../components/PasswordInput";

const TERMS_VERSION = "2026-02-27";

export default function VisitorRegister() {
  const { registerClient } = useAuth();
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", password: "" });
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validatePassword(form.password);
    if (!v.ok) {
      setError(v.message);
      return;
    }
    if (form.password !== passwordConfirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (!accepted) {
      setError("Veuillez accepter les conditions pour continuer");
      return;
    }
    setLoading(true);
    try {
      await registerClient({ ...form, termsAccepted: true, termsVersion: TERMS_VERSION });
      // Email verification is now mandatory — redirect to the dedicated screen
      // explaining that the account is pending email confirmation.
      toast.success("Compte créé. Vérifiez votre email pour activer votre compte.");
      nav(`/check-email?email=${encodeURIComponent(form.email)}&role=client`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur");
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-8 py-16 max-w-md">
      <Link to={`/register${loc.search}`} className="text-sm text-subtle hover:text-ink inline-flex items-center gap-1" data-testid="back-chooser-link">← Retour</Link>
      <p className="overline mb-2 mt-4">Visiteur</p>
      <h1 className="font-display text-4xl font-black tracking-tight text-ink">Créer un compte visiteur</h1>
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Prénom</Label>
            <Input required value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} className="h-12 mt-1.5" data-testid="prenom-input" />
          </div>
          <div>
            <Label>Nom</Label>
            <Input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="h-12 mt-1.5" data-testid="nom-input" />
          </div>
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 mt-1.5" data-testid="email-input" />
        </div>
        <div>
          <Label>Mot de passe</Label>
          <PasswordInput
            required
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="h-12 mt-1.5"
            data-testid="password-input"
          />
          <PasswordRules pwd={form.password} />
        </div>
        <div>
          <Label>Confirmer le mot de passe</Label>
          <PasswordInput
            required
            autoComplete="new-password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            className="h-12 mt-1.5"
            data-testid="password-confirm-input"
          />
          {passwordConfirm && form.password !== passwordConfirm && (
            <p className="mt-1 text-xs text-destructive" data-testid="password-confirm-error">
              Les mots de passe ne correspondent pas.
            </p>
          )}
        </div>

        <div className="flex items-start gap-3 border border-border p-3 bg-surface/50">
          <Checkbox
            id="terms"
            checked={accepted}
            onCheckedChange={(v) => { setAccepted(!!v); if (v) setError(""); }}
            className="mt-0.5"
            data-testid="terms-checkbox"
          />
          <label htmlFor="terms" className="text-sm text-ink leading-snug cursor-pointer">
            J'accepte les <Link to="/legal#cgu" target="_blank" className="underline text-brand">Conditions Générales d'Utilisation</Link>, les <Link to="/legal#mentions-legales" target="_blank" className="underline text-brand">Mentions légales</Link> et la <Link to="/legal#confidentialite" target="_blank" className="underline text-brand">Politique de confidentialité</Link>.
          </label>
        </div>
        {error && <p className="text-sm text-destructive" data-testid="terms-error">{error}</p>}

        <Button type="submit" disabled={loading} className="w-full h-12 bg-brand hover:bg-brand-hover rounded-md text-white font-medium" data-testid="submit-btn">
          {loading ? "…" : "Créer mon compte"}
        </Button>
      </form>
      <p className="mt-6 text-sm text-subtle">
        Déjà un compte ? <Link to={`/visitor/login${loc.search}`} className="text-brand font-medium">Se connecter</Link>
      </p>
      <WelcomePopup
        open={welcomeOpen}
        role="visiteur"
        onClose={() => {
          setWelcomeOpen(false);
          const nextParam = new URLSearchParams(loc.search).get("next");
          nav(nextParam || "/");
        }}
      />
    </div>
  );
}

function PasswordRules({ pwd }) {
  const r = passwordCheckBreakdown(pwd || "");
  const items = [
    { key: "length", label: "8 caractères minimum", ok: r.length },
    { key: "letter", label: "1 lettre", ok: r.letter },
    { key: "digit", label: "1 chiffre", ok: r.digit },
    { key: "special", label: "1 caractère spécial (ex : *, ., !, /)", ok: r.special },
  ];
  return (
    <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="password-rules">
      {items.map((it) => (
        <li key={it.key} className={`flex items-center gap-1.5 ${it.ok ? "text-[#0A7F2E]" : "text-subtle"}`}>
          {it.ok ? <Check size={12} /> : <X size={12} />} {it.label}
        </li>
      ))}
    </ul>
  );
}
